/**
 * Event, bomb, hit feedback, reload, and UI sound designs for Counter Pump.
 *
 * 22 sounds covering:
 * - Hit feedback: bell-like hit confirm, sharp "dink" headshot confirm
 * - Reload: magazine release + insertion
 * - Bomb: plant sequence, ticks, massive explosion, defuse jingle
 * - Events: round start horn, death rumble, dry fire click
 * - UI: radio beep
 * - Extra: fire extinguish
 */

import type { SoundDesign, SoundLayer } from './sound-designer';
import { WEAPONS, type WeaponId } from '../core/config';
import { audioManager } from './audio-manager';

const SR = 44100;

export type ReloadPhase = 'start' | 'end';
export type RoundEndStingName = 'eco_win_sting' | 'ace_sting' | 'clutch_sting' | 'comeback_sting';

export interface RoundEndStingContext {
  winnerTeam: number;
  localTeam: number;
  localPlayerRoundKills?: number;
  localTeamStartedEco?: boolean;
  localAliveAtEnd?: number;
  enemyAliveAtEnd?: number;
  localScoreBeforeRound?: number;
  enemyScoreBeforeRound?: number;
}

export function resolveReloadVariant(weaponId: string | undefined, phase: ReloadPhase): string {
  const fallback = phase === 'start' ? 'reload_start' : 'reload_end';
  if (!weaponId || !(weaponId in WEAPONS)) return fallback;
  const cfg = WEAPONS[weaponId as WeaponId];
  if (cfg.type === 'melee') return fallback;
  return `${cfg.type}_${fallback}`;
}

/**
 * SRV-W2: Play the registered `splash` Howl positionally at the player's
 * water-entry coordinates. The Howl is registered in
 * `AudioManager.registerGameSounds()` and matches the design declared in
 * `audio/sound-designer.ts` (`splashDesign`).
 */
export function playWaterSplash(position: { x: number; y: number; z: number }): void {
  audioManager.playAt('splash', position, 0.7);
}

/**
 * GP-W1: 3D-positional fake gunshot for decoy grenades. The decoy emits this
 * every ~0.5 s while burning (server: `grenade.go` decoy update loop), giving
 * enemies a positional cue they can chase. Routes through the existing
 * `distant_gunfire` design so the timbre matches a real gunshot from afar.
 */
export function playDecoyGunfire(position: { x: number; y: number; z: number }): void {
  audioManager.playAt('distant_gunfire', position, 0.6);
}

/**
 * GP-W2: Short pain grunt for the local player. Used by fall damage and any
 * other "self-hurt" event that needs an audio confirmation distinct from the
 * directional damage indicator.
 */
export function playPainGrunt(): void {
  audioManager.play('pain_grunt', 0.6);
}

/**
 * UI-M3: Soft heartbeat-style beep on the rising edge of the local player's
 * health crossing under the low-HP threshold. Caller is responsible for
 * debouncing — this helper has no internal state.
 */
export function playLowHPBeep(): void {
  audioManager.play('low_hp_beep', 0.5);
}

/**
 * UI-M3: Crisp click on the rising edge of low-ammo. Plays only on
 * transition; caller debounces.
 */
export function playLowAmmoBeep(): void {
  audioManager.play('low_ammo_beep', 0.4);
}

export function pickRoundEndSting(ctx: RoundEndStingContext): RoundEndStingName | null {
  if (ctx.localTeam === 0 || ctx.winnerTeam !== ctx.localTeam) return null;
  if ((ctx.localPlayerRoundKills ?? 0) >= 5) return 'ace_sting';
  if ((ctx.localAliveAtEnd ?? 0) === 1 && (ctx.enemyAliveAtEnd ?? 0) > 0) return 'clutch_sting';
  if (
    typeof ctx.localScoreBeforeRound === 'number' &&
    typeof ctx.enemyScoreBeforeRound === 'number' &&
    ctx.enemyScoreBeforeRound - ctx.localScoreBeforeRound >= 4
  ) {
    return 'comeback_sting';
  }
  if (ctx.localTeamStartedEco) return 'eco_win_sting';
  return null;
}

function osc(
  freq: number,
  waveform: OscillatorType,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'oscillator', waveform, frequency: freq,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function noise(
  noiseType: 'white' | 'pink' | 'brown',
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'noise', noiseType, attack, decay, sustain, release, gain, ...opts,
  };
}

function fnoise(
  noiseType: 'white' | 'pink' | 'brown',
  filterType: BiquadFilterType,
  filterFreq: number,
  filterQ: number,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'filtered-noise', noiseType, filterType, filterFreq, filterQ,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function impulse(
  gain: number,
  duration: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'impulse', attack: 0, decay: 0, sustain: 1, release: 0,
    gain, duration, ...opts,
  };
}

export function getEventSoundDesigns(): Map<string, SoundDesign> {
  const designs = new Map<string, SoundDesign>();

  // ─── HIT CONFIRM ──────────────────────────────────────────────────
  designs.set('hit_confirm', {
    name: 'hit_confirm',
    duration: 0.08,
    sampleRate: SR,
    layers: [
      osc(800, 'sine', 0.4, 0.001, 0.015, 0.1, 0.03),
      osc(1200, 'sine', 0.15, 0.001, 0.01, 0.06, 0.025),
      osc(400, 'triangle', 0.1, 0.002, 0.01, 0.04, 0.02),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  });

  // ─── HEADSHOT CONFIRM ─────────────────────────────────────────────
  designs.set('headshot_confirm', {
    name: 'headshot_confirm',
    duration: 0.1,
    sampleRate: SR,
    layers: [
      impulse(0.5, 0.003),
      osc(1400, 'sine', 0.5, 0.001, 0.02, 0.1, 0.035),
      osc(2000, 'sine', 0.2, 0.001, 0.012, 0.06, 0.025),
      osc(700, 'triangle', 0.15, 0.002, 0.01, 0.05, 0.025),
      fnoise('white', 'highpass', 3000, 2, 0.1, 0.001, 0.008, 0.04, 0.02),
    ],
    compression: { threshold: 0.4, ratio: 3, attack: 0.001, release: 0.012 },
  });

  // ─── RELOAD START ─────────────────────────────────────────────────
  designs.set('reload_start', {
    name: 'reload_start',
    duration: 0.12,
    sampleRate: SR,
    layers: [
      impulse(0.5, 0.004),
      osc(400, 'square', 0.2, 0.001, 0.01, 0.04, 0.02),
      fnoise('white', 'bandpass', 2000, 2, 0.18, 0.001, 0.015, 0.06, 0.03),
      osc(200, 'triangle', 0.12, 0.002, 0.015, 0.04, 0.025),
      fnoise('pink', 'lowpass', 1500, 1, 0.08, 0.005, 0.02, 0.03, 0.03,
        { startTime: 0.03, duration: 0.07 }),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  });

  // ─── RELOAD END ───────────────────────────────────────────────────
  designs.set('reload_end', {
    name: 'reload_end',
    duration: 0.15,
    sampleRate: SR,
    layers: [
      impulse(0.6, 0.005),
      osc(300, 'square', 0.25, 0.001, 0.01, 0.05, 0.02),
      osc(600, 'triangle', 0.12, 0.001, 0.008, 0.04, 0.015),
      fnoise('white', 'bandpass', 2500, 2, 0.2, 0.001, 0.012, 0.06, 0.03),
      impulse(0.35, 0.004, { startTime: 0.05, duration: 0.04 }),
      osc(500, 'square', 0.15, 0.001, 0.008, 0.03, 0.015,
        { startTime: 0.05, duration: 0.06 }),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.012 },
  });

  // ─── BOMB PLANT ───────────────────────────────────────────────────
  designs.set('bomb_plant', {
    name: 'bomb_plant',
    duration: 3.2,
    sampleRate: SR,
    layers: [
      osc(600, 'sine', 0.25, 0.01, 0.05, 0.3, 0.1,
        { startTime: 0.0, duration: 0.35 }),
      osc(650, 'sine', 0.25, 0.01, 0.05, 0.3, 0.1,
        { startTime: 0.4, duration: 0.35 }),
      osc(700, 'sine', 0.27, 0.01, 0.05, 0.3, 0.1,
        { startTime: 0.8, duration: 0.35 }),
      osc(750, 'sine', 0.27, 0.01, 0.05, 0.3, 0.1,
        { startTime: 1.2, duration: 0.35 }),
      osc(800, 'sine', 0.3, 0.01, 0.05, 0.3, 0.1,
        { startTime: 1.6, duration: 0.35 }),
      osc(850, 'sine', 0.3, 0.01, 0.05, 0.3, 0.1,
        { startTime: 2.0, duration: 0.35 }),
      osc(900, 'sine', 0.32, 0.01, 0.05, 0.3, 0.1,
        { startTime: 2.4, duration: 0.35 }),
      osc(1000, 'sine', 0.35, 0.01, 0.05, 0.3, 0.1,
        { startTime: 2.8, duration: 0.35 }),
      fnoise('white', 'highpass', 6000, 3, 0.02, 0.1, 0.1, 0.8, 0.3),
    ],
  });

  // ─── BOMB TICK ────────────────────────────────────────────────────
  designs.set('bomb_tick', {
    name: 'bomb_tick',
    duration: 0.08,
    sampleRate: SR,
    layers: [
      impulse(0.4, 0.003),
      osc(800, 'square', 0.3, 0.001, 0.01, 0.04, 0.02),
      osc(1600, 'sine', 0.1, 0.001, 0.005, 0.02, 0.015),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.008 },
  });

  // ─── BOMB TICK FAST ───────────────────────────────────────────────
  designs.set('bomb_tick_fast', {
    name: 'bomb_tick_fast',
    duration: 0.05,
    sampleRate: SR,
    layers: [
      impulse(0.45, 0.002),
      osc(1200, 'square', 0.35, 0.001, 0.008, 0.03, 0.012),
      osc(2000, 'sine', 0.12, 0.001, 0.004, 0.02, 0.01),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.006 },
  });

  // ─── BOMB EXPLODE ─────────────────────────────────────────────────
  designs.set('bomb_explode', {
    name: 'bomb_explode',
    duration: 3.5,
    sampleRate: SR,
    layers: [
      impulse(0.98, 0.015),
      osc(20, 'sine', 0.65, 0.005, 0.15, 0.08, 1.2),
      osc(50, 'sine', 0.5, 0.003, 0.1, 0.1, 0.9),
      osc(100, 'sawtooth', 0.35, 0.002, 0.06, 0.06, 0.6, { endFrequency: 30 }),
      noise('white', 0.45, 0.001, 0.05, 0.08, 0.8),
      fnoise('pink', 'bandpass', 1000, 1, 0.35, 0.002, 0.06, 0.08, 0.9),
      noise('brown', 0.3, 0.01, 0.08, 0.06, 1.2),
      fnoise('white', 'highpass', 5000, 2, 0.15, 0.001, 0.015, 0.03, 0.1,
        { startTime: 0.0, duration: 0.2 }),
      osc(40, 'sine', 0.2, 0.05, 0.1, 0.04, 0.8,
        { startTime: 0.3, duration: 2.0 }),
      noise('brown', 0.15, 0.1, 0.2, 0.04, 1.0,
        { startTime: 0.5, duration: 2.5 }),
    ],
    compression: { threshold: 0.25, ratio: 6, attack: 0.001, release: 0.05 },
    distortion: { amount: 1.6, oversample: 'none' },
  });

  // ─── BOMB DEFUSED ─────────────────────────────────────────────────
  designs.set('bomb_defused', {
    name: 'bomb_defused',
    duration: 0.5,
    sampleRate: SR,
    layers: [
      osc(523, 'sine', 0.3, 0.005, 0.03, 0.4, 0.08,
        { startTime: 0.0, duration: 0.15 }),
      osc(659, 'sine', 0.3, 0.005, 0.03, 0.4, 0.08,
        { startTime: 0.12, duration: 0.15 }),
      osc(784, 'sine', 0.35, 0.005, 0.03, 0.5, 0.12,
        { startTime: 0.24, duration: 0.2 }),
      osc(1046, 'sine', 0.08, 0.005, 0.02, 0.3, 0.06,
        { startTime: 0.0, duration: 0.12 }),
      osc(1318, 'sine', 0.08, 0.005, 0.02, 0.3, 0.06,
        { startTime: 0.12, duration: 0.12 }),
      osc(1568, 'sine', 0.1, 0.005, 0.02, 0.4, 0.1,
        { startTime: 0.24, duration: 0.18 }),
    ],
  });

  // ─── ROUND START ──────────────────────────────────────────────────
  designs.set('round_start', {
    name: 'round_start',
    duration: 0.4,
    sampleRate: SR,
    layers: [
      osc(440, 'sawtooth', 0.35, 0.01, 0.05, 0.5, 0.1),
      osc(880, 'sawtooth', 0.15, 0.01, 0.04, 0.4, 0.1),
      osc(330, 'sawtooth', 0.2, 0.01, 0.05, 0.45, 0.1),
      fnoise('pink', 'bandpass', 500, 1, 0.08, 0.02, 0.05, 0.3, 0.1),
    ],
    compression: { threshold: 0.4, ratio: 3, attack: 0.002, release: 0.02 },
    lowpass: 6000,
  });

  // ─── DEATH ────────────────────────────────────────────────────────
  designs.set('death', {
    name: 'death',
    duration: 0.5,
    sampleRate: SR,
    layers: [
      osc(60, 'sine', 0.35, 0.01, 0.08, 0.2, 0.2),
      osc(30, 'sine', 0.2, 0.02, 0.1, 0.15, 0.25),
      noise('brown', 0.12, 0.02, 0.06, 0.1, 0.2),
      osc(40, 'sine', 0.25, 0.005, 0.04, 0.05, 0.06,
        { startTime: 0.05, duration: 0.12 }),
    ],
    lowpass: 2000,
    compression: { threshold: 0.4, ratio: 3, attack: 0.002, release: 0.02 },
  });

  // ─── DRY FIRE ─────────────────────────────────────────────────────
  designs.set('dry_fire', {
    name: 'dry_fire',
    duration: 0.03,
    sampleRate: SR,
    layers: [
      impulse(0.55, 0.002),
      osc(600, 'square', 0.25, 0.001, 0.005, 0.02, 0.008),
      osc(1500, 'sine', 0.08, 0.001, 0.003, 0.01, 0.005),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.004 },
  });

  // ─── RADIO BEEP ───────────────────────────────────────────────────
  designs.set('radio_beep', {
    name: 'radio_beep',
    duration: 0.2,
    sampleRate: SR,
    layers: [
      osc(1000, 'sine', 0.2, 0.005, 0.02, 0.3, 0.04,
        { startTime: 0.0, duration: 0.09 }),
      osc(1400, 'sine', 0.2, 0.005, 0.02, 0.3, 0.04,
        { startTime: 0.09, duration: 0.09 }),
      fnoise('white', 'highpass', 5000, 2, 0.04, 0.002, 0.005, 0.15, 0.03),
    ],
  });

  // ─── FIRE EXTINGUISH ──────────────────────────────────────────────
  designs.set('fire_extinguish', {
    name: 'fire_extinguish',
    duration: 0.6,
    sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 3000, 1.5, 0.3, 0.02, 0.05, 0.15, 0.25),
      fnoise('pink', 'highpass', 2000, 1, 0.15, 0.01, 0.04, 0.1, 0.2),
      osc(1500, 'sine', 0.06, 0.02, 0.03, 0.08, 0.15, { endFrequency: 800 }),
      fnoise('brown', 'lowpass', 800, 0.7, 0.08, 0.03, 0.05, 0.06, 0.15),
    ],
    compression: { threshold: 0.4, ratio: 3, attack: 0.002, release: 0.02 },
  });

  // ═══════════════════════════════════════════════════════════════════
  // Phase 10 — Audio Expansion (8 new sounds)
  // ═══════════════════════════════════════════════════════════════════

  // ─── WEAPON SWITCH ──────────────────────────────────────────────
  // Short metallic click — white noise burst (20ms) with bandpass filter at 3000Hz
  designs.set('weapon_switch', {
    name: 'weapon_switch',
    duration: 0.04,
    sampleRate: SR,
    layers: [
      impulse(0.45, 0.003),
      fnoise('white', 'bandpass', 3000, 2.5, 0.35, 0.001, 0.005, 0.02, 0.012),
      osc(1800, 'sine', 0.08, 0.001, 0.003, 0.01, 0.008),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.005 },
  });

  // ─── BOLT ACTION ────────────────────────────────────────────────
  // Mechanical slide — two noise bursts (50ms each, 100ms apart) with lowpass at 2000Hz
  designs.set('bolt_action', {
    name: 'bolt_action',
    duration: 0.25,
    sampleRate: SR,
    layers: [
      // First burst — pull back
      impulse(0.4, 0.004, { startTime: 0.0, duration: 0.05 }),
      fnoise('white', 'lowpass', 2000, 1.5, 0.3, 0.001, 0.01, 0.03, 0.02,
        { startTime: 0.0, duration: 0.06 }),
      osc(300, 'square', 0.12, 0.001, 0.008, 0.02, 0.015,
        { startTime: 0.0, duration: 0.05 }),
      // Second burst — push forward
      impulse(0.45, 0.004, { startTime: 0.1, duration: 0.05 }),
      fnoise('white', 'lowpass', 2000, 1.5, 0.32, 0.001, 0.01, 0.03, 0.02,
        { startTime: 0.1, duration: 0.06 }),
      osc(350, 'square', 0.14, 0.001, 0.008, 0.02, 0.015,
        { startTime: 0.1, duration: 0.05 }),
    ],
    compression: { threshold: 0.45, ratio: 2.5, attack: 0.001, release: 0.008 },
  });

  // ─── UI SELECT ──────────────────────────────────────────────────
  // Clean beep — sine wave at 880Hz, 50ms, gain envelope 0->0.3->0
  designs.set('ui_select', {
    name: 'ui_select',
    duration: 0.06,
    sampleRate: SR,
    layers: [
      osc(880, 'sine', 0.3, 0.002, 0.01, 0.3, 0.02),
      osc(1760, 'sine', 0.06, 0.002, 0.008, 0.2, 0.015),
    ],
  });

  // ─── UI HOVER ───────────────────────────────────────────────────
  // Subtle tick — sine wave at 1200Hz, 15ms, very low gain (0.1)
  designs.set('ui_hover', {
    name: 'ui_hover',
    duration: 0.02,
    sampleRate: SR,
    layers: [
      osc(1200, 'sine', 0.1, 0.001, 0.003, 0.1, 0.006),
    ],
  });

  // ─── UI ERROR ───────────────────────────────────────────────────
  // Buzz — square wave at 200Hz, 150ms, descending pitch to 100Hz
  designs.set('ui_error', {
    name: 'ui_error',
    duration: 0.18,
    sampleRate: SR,
    layers: [
      osc(200, 'square', 0.25, 0.002, 0.02, 0.4, 0.04, { endFrequency: 100 }),
      osc(400, 'square', 0.08, 0.002, 0.015, 0.3, 0.03, { endFrequency: 200 }),
      fnoise('white', 'highpass', 2000, 1, 0.04, 0.005, 0.01, 0.1, 0.03),
    ],
    compression: { threshold: 0.4, ratio: 3, attack: 0.002, release: 0.015 },
  });

  // ─── UI NOTIFICATION ────────────────────────────────────────────
  // Two-tone chime — sine 660Hz (80ms) then 880Hz (120ms)
  designs.set('ui_notification', {
    name: 'ui_notification',
    duration: 0.25,
    sampleRate: SR,
    layers: [
      osc(660, 'sine', 0.25, 0.003, 0.015, 0.4, 0.03,
        { startTime: 0.0, duration: 0.1 }),
      osc(1320, 'sine', 0.06, 0.003, 0.01, 0.3, 0.02,
        { startTime: 0.0, duration: 0.08 }),
      osc(880, 'sine', 0.28, 0.003, 0.015, 0.5, 0.05,
        { startTime: 0.1, duration: 0.14 }),
      osc(1760, 'sine', 0.07, 0.003, 0.01, 0.35, 0.03,
        { startTime: 0.1, duration: 0.12 }),
    ],
  });

  // ─── DISTANT GUNFIRE ────────────────────────────────────────────
  // Muffled boom — noise burst (200ms) with heavy lowpass at 400Hz, low gain (0.15)
  designs.set('distant_gunfire', {
    name: 'distant_gunfire',
    duration: 0.3,
    sampleRate: SR,
    layers: [
      fnoise('white', 'lowpass', 400, 0.8, 0.15, 0.001, 0.02, 0.05, 0.15),
      fnoise('brown', 'lowpass', 300, 0.7, 0.1, 0.002, 0.03, 0.04, 0.12),
      osc(60, 'sine', 0.12, 0.003, 0.02, 0.04, 0.1),
    ],
    lowpass: 500,
    compression: { threshold: 0.4, ratio: 2.5, attack: 0.002, release: 0.02 },
  });

  // ─── WEAPON INSPECT ─────────────────────────────────────────────
  // Soft handling — white noise (60ms) with bandpass at 1500Hz, very low gain
  designs.set('weapon_inspect', {
    name: 'weapon_inspect',
    duration: 0.08,
    sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 1500, 1.5, 0.12, 0.002, 0.01, 0.03, 0.025),
      osc(800, 'sine', 0.04, 0.002, 0.008, 0.02, 0.02),
      fnoise('pink', 'lowpass', 2000, 1, 0.06, 0.003, 0.012, 0.02, 0.02),
    ],
  });

  // -------------------------------------------------------------------------
  // W8: Per-weapon-class reload + inspect variants. Each is a tweaked
  // version of the universal envelope so callers using `playReloadStart` /
  // `playReloadEnd` / `playWeaponInspect` get a class-distinct cue. When new
  // recorded samples land they can simply replace these designs by `name`.
  // -------------------------------------------------------------------------

  // Pistol — quick slide, lighter mechanism.
  designs.set('pistol_reload_start', {
    name: 'pistol_reload_start', duration: 0.10, sampleRate: SR,
    layers: [
      impulse(0.45, 0.003),
      osc(550, 'square', 0.18, 0.001, 0.008, 0.03, 0.018),
      fnoise('white', 'bandpass', 2400, 2, 0.16, 0.001, 0.012, 0.05, 0.025),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  });
  designs.set('pistol_reload_end', {
    name: 'pistol_reload_end', duration: 0.12, sampleRate: SR,
    layers: [
      impulse(0.5, 0.004),
      osc(420, 'square', 0.22, 0.001, 0.008, 0.04, 0.018),
      osc(720, 'triangle', 0.1, 0.001, 0.006, 0.03, 0.012),
      fnoise('white', 'bandpass', 2700, 2, 0.18, 0.001, 0.01, 0.05, 0.025),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.012 },
  });
  designs.set('pistol_inspect', {
    name: 'pistol_inspect', duration: 0.07, sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 1800, 1.5, 0.10, 0.002, 0.008, 0.025, 0.022),
      osc(900, 'sine', 0.035, 0.002, 0.006, 0.018, 0.018),
    ],
  });

  // Rifle — heavier magazine click, longer envelope.
  designs.set('rifle_reload_start', {
    name: 'rifle_reload_start', duration: 0.16, sampleRate: SR,
    layers: [
      impulse(0.6, 0.005),
      osc(280, 'square', 0.26, 0.001, 0.014, 0.06, 0.028),
      fnoise('white', 'bandpass', 1700, 2, 0.20, 0.001, 0.018, 0.08, 0.034),
      osc(160, 'triangle', 0.15, 0.002, 0.018, 0.05, 0.03),
      fnoise('pink', 'lowpass', 1300, 1, 0.10, 0.005, 0.024, 0.04, 0.034,
        { startTime: 0.04, duration: 0.09 }),
    ],
    compression: { threshold: 0.5, ratio: 2.8, attack: 0.001, release: 0.012 },
  });
  designs.set('rifle_reload_end', {
    name: 'rifle_reload_end', duration: 0.18, sampleRate: SR,
    layers: [
      impulse(0.7, 0.006),
      osc(240, 'square', 0.30, 0.001, 0.012, 0.06, 0.024),
      osc(540, 'triangle', 0.14, 0.001, 0.010, 0.05, 0.018),
      fnoise('white', 'bandpass', 2200, 2, 0.22, 0.001, 0.014, 0.07, 0.034),
      impulse(0.42, 0.005, { startTime: 0.06, duration: 0.05 }),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.014 },
  });
  designs.set('rifle_inspect', {
    name: 'rifle_inspect', duration: 0.10, sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 1300, 1.5, 0.14, 0.002, 0.012, 0.04, 0.030),
      osc(700, 'sine', 0.05, 0.002, 0.010, 0.025, 0.025),
      fnoise('pink', 'lowpass', 1800, 1, 0.07, 0.003, 0.014, 0.025, 0.030),
    ],
  });

  // Sniper — bolt cycle, distinct two-stroke (open + close).
  designs.set('sniper_reload_start', {
    name: 'sniper_reload_start', duration: 0.22, sampleRate: SR,
    layers: [
      impulse(0.55, 0.006),
      osc(220, 'square', 0.22, 0.001, 0.018, 0.06, 0.040),
      fnoise('white', 'bandpass', 1500, 2, 0.18, 0.001, 0.022, 0.10, 0.040),
      impulse(0.5, 0.005, { startTime: 0.10, duration: 0.06 }),
      osc(310, 'square', 0.18, 0.001, 0.014, 0.05, 0.030, { startTime: 0.10, duration: 0.10 }),
    ],
    compression: { threshold: 0.5, ratio: 2.8, attack: 0.001, release: 0.014 },
  });
  designs.set('sniper_reload_end', {
    name: 'sniper_reload_end', duration: 0.18, sampleRate: SR,
    layers: [
      impulse(0.7, 0.007),
      osc(200, 'square', 0.30, 0.001, 0.014, 0.07, 0.028),
      osc(440, 'triangle', 0.12, 0.001, 0.012, 0.05, 0.020),
      fnoise('white', 'bandpass', 1900, 2, 0.20, 0.001, 0.016, 0.07, 0.034),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.014 },
  });
  designs.set('sniper_inspect', {
    name: 'sniper_inspect', duration: 0.12, sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 1100, 1.5, 0.16, 0.003, 0.014, 0.05, 0.034),
      osc(640, 'sine', 0.06, 0.002, 0.012, 0.03, 0.030),
      fnoise('pink', 'lowpass', 1600, 1, 0.08, 0.004, 0.016, 0.03, 0.036),
    ],
  });

  // SMG — fast tinny rattle.
  designs.set('smg_reload_start', {
    name: 'smg_reload_start', duration: 0.10, sampleRate: SR,
    layers: [
      impulse(0.42, 0.003),
      osc(620, 'square', 0.18, 0.001, 0.008, 0.03, 0.018),
      fnoise('white', 'bandpass', 2900, 2, 0.18, 0.001, 0.010, 0.05, 0.022),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.01 },
  });
  designs.set('smg_reload_end', {
    name: 'smg_reload_end', duration: 0.12, sampleRate: SR,
    layers: [
      impulse(0.5, 0.004),
      osc(360, 'square', 0.22, 0.001, 0.009, 0.04, 0.018),
      osc(820, 'triangle', 0.10, 0.001, 0.007, 0.03, 0.014),
      fnoise('white', 'bandpass', 3000, 2, 0.20, 0.001, 0.011, 0.05, 0.024),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.012 },
  });
  designs.set('smg_inspect', {
    name: 'smg_inspect', duration: 0.07, sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 2100, 1.5, 0.10, 0.002, 0.008, 0.025, 0.022),
      osc(1000, 'sine', 0.04, 0.002, 0.006, 0.02, 0.020),
    ],
  });

  // Shotgun — pump action, low + heavy.
  designs.set('shotgun_reload_start', {
    name: 'shotgun_reload_start', duration: 0.20, sampleRate: SR,
    layers: [
      impulse(0.7, 0.008),
      osc(150, 'square', 0.32, 0.001, 0.020, 0.07, 0.040),
      fnoise('brown', 'lowpass', 900, 1.5, 0.20, 0.002, 0.026, 0.10, 0.044),
      osc(80, 'sine', 0.20, 0.002, 0.024, 0.06, 0.045),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.014 },
  });
  designs.set('shotgun_reload_end', {
    name: 'shotgun_reload_end', duration: 0.22, sampleRate: SR,
    layers: [
      impulse(0.75, 0.009),
      osc(130, 'square', 0.34, 0.001, 0.018, 0.07, 0.032),
      osc(330, 'triangle', 0.16, 0.001, 0.014, 0.06, 0.022),
      fnoise('white', 'bandpass', 1500, 2, 0.22, 0.001, 0.020, 0.08, 0.036),
      impulse(0.45, 0.006, { startTime: 0.08, duration: 0.06 }),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.014 },
  });
  designs.set('shotgun_inspect', {
    name: 'shotgun_inspect', duration: 0.11, sampleRate: SR,
    layers: [
      fnoise('brown', 'lowpass', 800, 1, 0.16, 0.003, 0.014, 0.04, 0.034),
      osc(300, 'sine', 0.06, 0.003, 0.012, 0.03, 0.030),
    ],
  });

  // Melee (knife) — wood-on-leather flick.
  designs.set('melee_inspect', {
    name: 'melee_inspect', duration: 0.10, sampleRate: SR,
    layers: [
      fnoise('white', 'highpass', 4000, 1.5, 0.12, 0.001, 0.008, 0.03, 0.030),
      fnoise('pink', 'bandpass', 3000, 1, 0.08, 0.002, 0.012, 0.04, 0.030,
        { startTime: 0.04, duration: 0.06 }),
      osc(1200, 'sine', 0.05, 0.002, 0.008, 0.02, 0.025),
    ],
  });

  // Slice H — round-end contextual victory stings.
  designs.set('eco_win_sting', {
    name: 'eco_win_sting',
    duration: 1.1,
    sampleRate: SR,
    layers: [
      osc(220, 'triangle', 0.18, 0.004, 0.03, 0.5, 0.16, { startTime: 0.0, duration: 0.4 }),
      osc(330, 'triangle', 0.22, 0.004, 0.03, 0.5, 0.18, { startTime: 0.18, duration: 0.45 }),
      osc(660, 'sine', 0.16, 0.003, 0.02, 0.45, 0.18, { startTime: 0.48, duration: 0.5 }),
      fnoise('pink', 'bandpass', 1200, 1.2, 0.08, 0.01, 0.04, 0.25, 0.25),
    ],
    compression: { threshold: 0.45, ratio: 2.5, attack: 0.002, release: 0.02 },
  });

  designs.set('ace_sting', {
    name: 'ace_sting',
    duration: 1.35,
    sampleRate: SR,
    layers: [
      osc(523, 'sine', 0.24, 0.003, 0.03, 0.45, 0.15, { startTime: 0.0, duration: 0.28 }),
      osc(659, 'sine', 0.24, 0.003, 0.03, 0.45, 0.15, { startTime: 0.18, duration: 0.28 }),
      osc(784, 'sine', 0.26, 0.003, 0.03, 0.5, 0.18, { startTime: 0.36, duration: 0.34 }),
      osc(1046, 'triangle', 0.18, 0.003, 0.025, 0.5, 0.26, { startTime: 0.68, duration: 0.55 }),
      impulse(0.35, 0.004, { startTime: 0.66, duration: 0.04 }),
    ],
    compression: { threshold: 0.42, ratio: 3, attack: 0.001, release: 0.02 },
  });

  designs.set('clutch_sting', {
    name: 'clutch_sting',
    duration: 1.2,
    sampleRate: SR,
    layers: [
      osc(130, 'sawtooth', 0.20, 0.01, 0.05, 0.4, 0.22, { startTime: 0.0, duration: 0.5 }),
      osc(196, 'triangle', 0.18, 0.004, 0.03, 0.45, 0.2, { startTime: 0.3, duration: 0.45 }),
      osc(392, 'sine', 0.20, 0.004, 0.025, 0.5, 0.24, { startTime: 0.62, duration: 0.48 }),
      fnoise('white', 'highpass', 2800, 1.8, 0.06, 0.002, 0.008, 0.08, 0.08, { startTime: 0.58, duration: 0.16 }),
    ],
    lowpass: 5000,
    compression: { threshold: 0.44, ratio: 3, attack: 0.002, release: 0.02 },
  });

  // ─── LOW HP BEEP (UI-M3) ───────────────────────────────────────
  // Soft sine pulse around 380 Hz, ~120 ms total. Quiet enough to layer
  // under heartbeat ambience without masking gunshots.
  designs.set('low_hp_beep', {
    name: 'low_hp_beep',
    duration: 0.14,
    sampleRate: SR,
    layers: [
      osc(380, 'sine', 0.32, 0.005, 0.025, 0.4, 0.06, { startTime: 0.0, duration: 0.10 }),
      osc(760, 'sine', 0.10, 0.005, 0.020, 0.3, 0.04, { startTime: 0.0, duration: 0.08 }),
    ],
  });

  // ─── LOW AMMO BEEP (UI-M3) ─────────────────────────────────────
  // Tighter, higher click — square fundamental at 1100 Hz with a short
  // impulse, ~60 ms. Sits in a different band from low_hp_beep so layered
  // playback (low HP + low ammo) stays legible.
  designs.set('low_ammo_beep', {
    name: 'low_ammo_beep',
    duration: 0.07,
    sampleRate: SR,
    layers: [
      impulse(0.30, 0.002),
      osc(1100, 'square', 0.22, 0.001, 0.010, 0.3, 0.020),
      osc(2200, 'sine', 0.06, 0.001, 0.006, 0.2, 0.012),
    ],
    compression: { threshold: 0.45, ratio: 2.5, attack: 0.001, release: 0.006 },
  });

  // ─── PAIN GRUNT ─────────────────────────────────────────────────
  // GP-W2: Short vocal-like grunt — low-frequency oscillator + brown noise
  // body, ~180 ms total. Used on local player fall damage.
  designs.set('pain_grunt', {
    name: 'pain_grunt',
    duration: 0.22,
    sampleRate: SR,
    layers: [
      osc(140, 'sawtooth', 0.28, 0.005, 0.04, 0.4, 0.10, { endFrequency: 90 }),
      osc(280, 'triangle', 0.16, 0.004, 0.03, 0.3, 0.08, { endFrequency: 170 }),
      noise('brown', 0.12, 0.005, 0.04, 0.2, 0.10),
      fnoise('pink', 'bandpass', 800, 1.5, 0.10, 0.005, 0.03, 0.15, 0.08),
    ],
    lowpass: 3500,
    compression: { threshold: 0.4, ratio: 3, attack: 0.002, release: 0.02 },
  });

  designs.set('comeback_sting', {
    name: 'comeback_sting',
    duration: 1.45,
    sampleRate: SR,
    layers: [
      osc(164, 'triangle', 0.16, 0.006, 0.04, 0.45, 0.2, { startTime: 0.0, duration: 0.45 }),
      osc(261, 'triangle', 0.20, 0.005, 0.035, 0.45, 0.22, { startTime: 0.28, duration: 0.48 }),
      osc(392, 'sine', 0.22, 0.004, 0.03, 0.5, 0.25, { startTime: 0.58, duration: 0.54 }),
      osc(784, 'sine', 0.10, 0.004, 0.025, 0.45, 0.22, { startTime: 0.82, duration: 0.5 }),
      fnoise('pink', 'lowpass', 1800, 0.8, 0.08, 0.02, 0.06, 0.25, 0.35),
    ],
    compression: { threshold: 0.45, ratio: 2.8, attack: 0.002, release: 0.025 },
  });

  return designs;
}
